// src/components/leitner/TasksHub.jsx
// Standalone multi-task portal for learners - First-class citizen via /tasks route
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { taskService } from '../../services/taskService';
import { useAuth } from '../../contexts/AuthContext';
import HeaderLogoTitle from '../shared/HeaderLogoTitle';
import UserProfile from '../shared/SharedUserProfile';
import { ProfileModal } from '../shared/ProfileModal';
import { AuthModal } from '../auth/AuthModal';
import {
  ClipboardList, Search, Target, Trophy, Clock, Play, ChevronRight,
  Globe, Lock, Users, Calendar, Timer, CheckCircle, BarChart3,
  Menu, X, LogIn, BookOpen, Filter, Sparkles, ArrowRight
} from 'lucide-react';

const GUEST_PROGRESS_KEY = 'pb_guest_task_progress';

const getGuestProgressForBook = (bookId) => {
  try {
    const stored = localStorage.getItem(GUEST_PROGRESS_KEY);
    if (!stored) return {};
    const all = JSON.parse(stored);
    const bookProgress = {};
    Object.keys(all).forEach(key => {
      if (key.startsWith(`${bookId}_`)) {
        const taskId = key.replace(`${bookId}_`, '');
        bookProgress[taskId] = all[key];
      }
    });
    return bookProgress;
  } catch {
    return {};
  }
};

const TasksHub = ({ bookData, bookId: propBookId }) => {
  const bookId = propBookId || bookData?.id;
  const navigate = useNavigate();
  const { user, userProfile, logout } = useAuth();

  const [tasks, setTasks] = useState([]);
  const [taskProgress, setTaskProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState('all');

  // Modal states
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isOwner = user?.uid === bookData?.ownerId;
  const customDomain = !!(bookData?.customDomain || bookData?.customPath);
  const basePath = bookData?.customPath ? `/${bookData.customPath}` : '';

  const styling = {
    primaryColor: bookData?.styling?.primaryColor || '#6366f1'
  };

  const getReadableHeaderText = (color) => {
    if (!color) return '#ffffff';
    const raw = String(color).replace('#', '').trim();
    const hex = raw.length === 3
      ? raw.split('').map((c) => c + c).join('')
      : raw;
    if (hex.length !== 6) return color;
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return luminance < 0.55 ? '#f8fafc' : color;
  };

  const customStyling = {
    primaryColor: getReadableHeaderText(bookData?.styling?.headerTextColor)
  };

  // Load tasks
  useEffect(() => {
    const loadTasks = async () => {
      if (!bookId) return;

      setLoading(true);
      try {
        let fetchedTasks;

        if (user) {
          fetchedTasks = await taskService.getAvailableTasks(bookId, user.email, user.uid);

          // Load progress for each task
          const progressMap = {};
          for (const task of fetchedTasks) {
            const progress = await taskService.getTaskProgress(bookId, task.id, user.uid);
            if (progress) {
              progressMap[task.id] = progress;
            }
          }
          setTaskProgress(progressMap);
        } else {
          // Guest: Only show public tasks that don't require sign-in
          fetchedTasks = await taskService.getPublicTasks(bookId);
          // Load guest progress from localStorage
          const guestProgress = getGuestProgressForBook(bookId);
          setTaskProgress(guestProgress);
        }

        setTasks(fetchedTasks);
      } catch (error) {
        console.error('Failed to load tasks:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTasks();
  }, [bookId, user]);

  const getTaskStatus = (task) => {
    const progress = taskProgress[task.id];
    if (!progress) return 'new';

    const questionIds = task.questionIds || [];
    if (questionIds.length === 0) return 'new';

    const answeredCount = Object.keys(progress.questions || {}).length;
    const masteredCount = Object.values(progress.questions || {})
      .filter(q => q.box >= 4).length;

    if (masteredCount === questionIds.length) return 'completed';
    if (answeredCount > 0) return 'in-progress';
    return 'new';
  };

  const getProgressPercent = (task) => {
    const progress = taskProgress[task.id];
    if (!progress) return 0;

    const questionIds = task.questionIds || [];
    if (questionIds.length === 0) return 0;

    const totalBoxScore = Object.values(progress.questions || {})
      .reduce((sum, q) => sum + (q.box || 1), 0);
    const maxScore = questionIds.length * 4;

    return Math.round((totalBoxScore / maxScore) * 100);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      task.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const status = getTaskStatus(task);

    if (filter === 'available') return matchesSearch && status !== 'completed';
    if (filter === 'completed') return matchesSearch && status === 'completed';
    if (filter === 'in-progress') return matchesSearch && status === 'in-progress';
    return matchesSearch;
  });

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public': return <Globe className="h-4 w-4 text-green-400" />;
      case 'organisation': return <Users className="h-4 w-4 text-blue-400" />;
      default: return <Lock className="h-4 w-4 text-amber-400" />;
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = date?.toDate?.() || new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const navigateToTask = (taskId) => {
    if (customDomain) {
      navigate(`${basePath}/tasks/${taskId}`);
    } else {
      navigate(`/books/${bookId}/tasks/${taskId}`);
    }
  };

  const navigateHome = () => {
    if (customDomain) {
      navigate(basePath || '/');
    } else {
      navigate(`/books/${bookId}`);
    }
  };

  // Stats
  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => getTaskStatus(t) === 'completed').length,
    inProgress: tasks.filter(t => getTaskStatus(t) === 'in-progress').length,
    new: tasks.filter(t => getTaskStatus(t) === 'new').length
  };

  // Header Component
  const HubHeader = () => (
    <header className="sticky top-0 z-40 backdrop-blur-md bg-slate-900/95 shadow-xl border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <HeaderLogoTitle
            isOwner={isOwner}
            bookData={bookData || { title: 'Leitner Practice Tasks', headerSettings: { logoHeight: 50 } }}
            customStyling={customStyling}
            navigate={navigate}
          />

          <div className="hidden md:flex items-center gap-3">
            {!user && (
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
              >
                <LogIn className="h-4 w-4" />
                Sign In
              </button>
            )}

            <UserProfile
              user={user}
              userProfile={userProfile}
              styling={styling}
              bookData={bookData || { headerSettings: { showProfile: true } }}
              isOwner={isOwner}
              navigate={navigate}
              logout={logout}
              isDarkMode={true}
              setShowAuthModal={setShowAuthModal}
              setShowProfileModal={setShowProfileModal}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          </div>

          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-lg hover:bg-white/10 transition-colors"
            >
              {mobileMenuOpen ? (
                <X className="h-6 w-6 text-white" />
              ) : (
                <Menu className="h-6 w-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden mt-3 pt-3 border-t border-white/20 space-y-3">
            {!user && (
              <button
                onClick={() => { setShowAuthModal(true); setMobileMenuOpen(false); }}
                className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <LogIn className="h-4 w-4" />
                Sign In to Track Progress
              </button>
            )}
            <UserProfile
              user={user}
              userProfile={userProfile}
              styling={styling}
              bookData={bookData || { headerSettings: { showProfile: true } }}
              isOwner={isOwner}
              navigate={navigate}
              logout={logout}
              isDarkMode={true}
              setShowAuthModal={setShowAuthModal}
              setShowProfileModal={setShowProfileModal}
              mobileMenuOpen={mobileMenuOpen}
              setMobileMenuOpen={setMobileMenuOpen}
            />
          </div>
        )}
      </div>
    </header>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950">
      <HubHeader />

      <div className="max-w-6xl mx-auto p-4 md:p-8">
        {/* Hero Section */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div
              className="p-3 rounded-xl"
              style={{ backgroundColor: `${styling.primaryColor}20` }}
            >
              <ClipboardList className="h-8 w-8" style={{ color: styling.primaryColor }} />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white">Leitner Pratice Tasks</h1>
              <p className="text-slate-400 mt-1">Master content through spaced repetition</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Tasks', value: stats.total, icon: BookOpen, color: 'slate' },
            { label: 'Completed', value: stats.completed, icon: Trophy, color: 'green' },
            { label: 'In Progress', value: stats.inProgress, icon: Target, color: 'amber' },
            { label: 'New', value: stats.new, icon: Sparkles, color: 'blue' }
          ].map(({ label, value, icon: Icon, color }) => (
            <div
              key={label}
              className="bg-slate-800/50 backdrop-blur rounded-xl p-4 border border-slate-700/50"
            >
              <Icon className={`h-5 w-5 mb-2 ${color === 'green' ? 'text-green-400' :
                color === 'amber' ? 'text-amber-400' :
                  color === 'blue' ? 'text-blue-400' : 'text-slate-400'
                }`} />
              <p className="text-2xl font-bold text-white">{value}</p>
              <p className="text-xs text-slate-400">{label}</p>
            </div>
          ))}
        </div>

        {/* Search & Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search tasks..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700/50 rounded-xl text-white placeholder-slate-400 focus:ring-2 focus:border-transparent transition-all"
              style={{ '--tw-ring-color': styling.primaryColor }}
            />
          </div>

          <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0">
            {[
              { key: 'all', label: 'All' },
              { key: 'available', label: 'Available' },
              { key: 'in-progress', label: 'In Progress' },
              { key: 'completed', label: 'Completed' }
            ].map(f => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${filter === f.key
                  ? 'text-white shadow-lg'
                  : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                  }`}
                style={filter === f.key ? { backgroundColor: styling.primaryColor } : {}}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tasks List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="animate-spin h-10 w-10 border-4 border-t-transparent rounded-full"
              style={{ borderColor: `${styling.primaryColor}40`, borderTopColor: styling.primaryColor }}
            />
          </div>
        ) : filteredTasks.length === 0 ? (
          <div className="text-center py-20 bg-slate-800/30 rounded-2xl border border-slate-700/50">
            <ClipboardList className="h-16 w-16 text-slate-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-300 mb-2">
              {searchQuery ? 'No tasks found' : 'No tasks available'}
            </h3>
            <p className="text-slate-400 mb-6">
              {!user
                ? 'Sign in to see all available tasks'
                : 'Check back later for new Leitner Practice tasks'
              }
            </p>
            {!user && (
              <button
                onClick={() => setShowAuthModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-white font-medium transition-all hover:opacity-90"
                style={{ backgroundColor: styling.primaryColor }}
              >
                <LogIn className="h-5 w-5" />
                Sign In
              </button>
            )}
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredTasks.map((task) => {
              const status = getTaskStatus(task);
              const progressPercent = getProgressPercent(task);

              return (
                <div
                  key={task.id}
                  onClick={() => navigateToTask(task.id)}
                  className="group bg-slate-800/50 backdrop-blur rounded-xl p-5 border border-slate-700/50 hover:border-slate-600/50 hover:bg-slate-800/70 transition-all cursor-pointer"
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                    {/* Task Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div
                          className="p-2.5 rounded-lg shrink-0 group-hover:scale-105 transition-transform"
                          style={{ backgroundColor: `${styling.primaryColor}20` }}
                        >
                          <Target className="h-5 w-5" style={{ color: styling.primaryColor }} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-white text-lg truncate group-hover:text-indigo-300 transition-colors">
                              {task.name}
                            </h3>
                            {getVisibilityIcon(task.visibility)}

                            {status === 'completed' && (
                              <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                                <CheckCircle className="h-3 w-3" />
                                Completed
                              </span>
                            )}
                            {status === 'in-progress' && (
                              <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                                In Progress
                              </span>
                            )}
                            {status === 'new' && (
                              <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full flex items-center gap-1">
                                <Sparkles className="h-3 w-3" />
                                New
                              </span>
                            )}
                          </div>

                          {task.description && (
                            <p className="text-slate-400 text-sm mt-1 line-clamp-2">
                              {task.description}
                            </p>
                          )}

                          {/* Meta Info */}
                          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-slate-400">
                            <span className="flex items-center gap-1">
                              <ClipboardList className="h-4 w-4" />
                              {task.questionIds?.length || 0} questions
                            </span>

                            {task.isTimeBound && (
                              <span className="flex items-center gap-1">
                                <Calendar className="h-4 w-4" />
                                {formatDate(task.startDate)} - {formatDate(task.endDate)}
                              </span>
                            )}

                            {task.timeLimitPerQuestion > 0 && (
                              <span className="flex items-center gap-1">
                                <Timer className="h-4 w-4" />
                                {task.timeLimitPerQuestion}s per question
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Progress & Action */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 lg:gap-6 mt-2 lg:mt-0">
                      {/* Progress Bar */}
                      <div className="w-full sm:flex-1 lg:w-40">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-slate-400">Progress</span>
                          <span className="text-white font-medium">{progressPercent}%</span>
                        </div>
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full transition-all duration-500"
                            style={{
                              width: `${progressPercent}%`,
                              backgroundColor: progressPercent === 100 ? '#22c55e' : styling.primaryColor
                            }}
                          />
                        </div>
                      </div>

                      {/* Action Button */}
                      <button
                        className="w-full sm:w-auto px-4 py-2.5 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all hover:opacity-90 hover:scale-105"
                        style={{ backgroundColor: styling.primaryColor }}
                      >
                        {status === 'new' ? (
                          <>
                            <Play className="h-4 w-4" />
                            Start
                          </>
                        ) : status === 'completed' ? (
                          <>
                            <BarChart3 className="h-4 w-4" />
                            Review
                          </>
                        ) : (
                          <>
                            <ArrowRight className="h-4 w-4" />
                            Continue
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Guest Banner */}
        {!user && tasks.length > 0 && (
          <div className="mt-8 p-6 bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-2xl border border-indigo-500/30">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-lg font-semibold text-white mb-1">
                  Track Your Progress Across Devices
                </h3>
                <p className="text-slate-300 text-sm">
                  Sign in to save your progress and access more tasks
                </p>
              </div>
              <button
                onClick={() => setShowAuthModal(true)}
                className="px-6 py-3 bg-white text-slate-900 rounded-lg font-medium hover:bg-slate-100 transition-colors flex items-center gap-2"
              >
                <LogIn className="h-5 w-5" />
                Sign In Now
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showProfileModal && (
        <ProfileModal
          onClose={() => setShowProfileModal(false)}
          onSave={() => setShowProfileModal(false)}
        />
      )}

      {showAuthModal && (
        <AuthModal
          isOpen={showAuthModal}
          defaultView="signin"
          onClose={() => setShowAuthModal(false)}
        />
      )}
    </div>
  );
};

export default TasksHub;
